import type { NextApiRequest, NextApiResponse } from 'next';

import { logger } from 'lib/logger';

import {
  SearchBloxResult,
  SearchBloxConfig,
  SearchQueryParams,
  SearchBloxResponse,
  StrictSearchOptions,
  StrictSearchClient,
} from './types';

const createBaseSearchUrl = (config: SearchBloxConfig) => {
  const { col, querykey, endpoint } = config;
  const searchUrl = new URL('/rest/v2/api/search', endpoint);
  searchUrl.searchParams.set('col', col);
  searchUrl.searchParams.set('querykey', querykey);
  searchUrl.searchParams.set('default', 'AND');

  return searchUrl;
};

const buildSearchRequestUrl = (searchUrl: URL, searchParams: SearchQueryParams) => {
  const { query, filter, page, pagesize, facets, sort, sortdir, sort1, sortdir1, extraParams } =
    searchParams;

  const reqUrl = new URL(searchUrl);

  reqUrl.searchParams.set('query', query || '*');

  if (filter) {
    reqUrl.searchParams.set('filter', filter);
  }

  if (page) {
    reqUrl.searchParams.set('page', page.toString());
  }

  if (pagesize) {
    reqUrl.searchParams.set('pagesize', pagesize.toString());
  }

  if (facets) {
    reqUrl.searchParams.set('facet', 'on');
    facets.forEach(({ fieldname, size }) => {
      reqUrl.searchParams.append('facet.field', fieldname);

      if (size) {
        reqUrl.searchParams.set(`f.${fieldname}.size`, size.toString());
      }
    });
  }

  if (sort) {
    reqUrl.searchParams.set('sort', sort);
  }

  if (sortdir) {
    reqUrl.searchParams.set('sortdir', sortdir);
  }

  if (sort1) {
    reqUrl.searchParams.set('sort1', sort1);
  }

  if (sortdir1) {
    reqUrl.searchParams.set('sortdir1', sortdir1);
  }

  if (extraParams) {
    Object.entries(extraParams).forEach(([name, value]) => {
      reqUrl.searchParams.set(name, value);
    });
  }

  return reqUrl;
};

export const createSearchClient = (config: SearchBloxConfig) => {
  const sbxLogger = logger.child({ id: 'searchblox' });

  const searchUrl = createBaseSearchUrl(config);

  const search = async <T extends SearchBloxResult>(
    searchParams: SearchQueryParams
  ): Promise<SearchBloxResponse<T>> => {
    try {
      const reqUrl = buildSearchRequestUrl(searchUrl, searchParams);
      const response = await fetch(reqUrl, {
        method: 'GET',
        headers: {
          'User-Agent': 'ONIWP SearchBlox',
        },
      });
      const json = (await response.json()) as SearchBloxResponse<T>;

      return json;
    } catch (err) {
      sbxLogger.error(err);

      return {
        query: '',
        filter: '',
        currentPage: 1,
        lastPage: 1,
        hits: 0,
        result: [],
        facets: [],
      };
    }
  };

  const autocomplete = async ({
    query,
    limit,
  }: {
    query?: string;
    limit?: number;
  }): Promise<string[]> => {
    try {
      const { col, querykey, endpoint } = config;

      const autocompleteUrl = new URL('/rest/v2/api/autocomplete', endpoint);
      autocompleteUrl.searchParams.set('col', col);
      autocompleteUrl.searchParams.set('querykey', querykey);

      const reqUrl = new URL(autocompleteUrl);
      reqUrl.searchParams.set('query', query || '*');
      if (limit) {
        reqUrl.searchParams.set('limit', limit.toString());
      }

      const response = await fetch(reqUrl, {
        method: 'GET',
        headers: {
          'User-Agent': 'ONIWP SearchBlox',
        },
      });

      return (await response.json()) as string[];
    } catch (err) {
      sbxLogger.error(err);

      return [];
    }
  };

  return {
    search,
    autocomplete,
  };
};

/**
 * Strict/throwing variant of the search client, used exclusively for facet-bearing queries
 * (see `src/data/queries/Search/disjunctiveFacets.ts`).
 *
 * Unlike the lenient client above (which swallows failures and fabricates an empty response),
 * this client THROWS on every transport-level failure class:
 * - transport errors (fetch rejection)
 * - transport-level timeout (`AbortSignal.timeout`; duration supplied per call by the caller —
 *   the lenient results path deliberately stays timeout-less)
 * - JSON parse errors
 * - non-OK HTTP status
 * - basic shape violations (missing `hits`, or missing/non-array `facets` when facets were
 *   requested)
 *
 * The client stays semantics-free: facet-*degradation* judgment (sentinel rules, sparseness)
 * lives with `parseFacetResponse` in the disjunctive facets helper, keeping `lib/searchblox`
 * a generic transport wrapper.
 */
export const createStrictSearchClient = (config: SearchBloxConfig): StrictSearchClient => {
  const searchUrl = createBaseSearchUrl(config);

  const search = async <T extends SearchBloxResult>(
    searchParams: SearchQueryParams,
    options?: StrictSearchOptions
  ): Promise<SearchBloxResponse<T>> => {
    const reqUrl = buildSearchRequestUrl(searchUrl, searchParams);
    const timeoutMs = options?.timeoutMs;

    const response = await fetch(reqUrl, {
      method: 'GET',
      headers: {
        'User-Agent': 'ONIWP SearchBlox',
      },
      signal: timeoutMs !== undefined ? AbortSignal.timeout(timeoutMs) : undefined,
    });

    if (!response.ok) {
      throw new Error(`SearchBlox responded with HTTP ${response.status}`);
    }

    const json = (await response.json()) as SearchBloxResponse<T>;

    if (typeof json?.hits !== 'number') {
      throw new Error('SearchBlox response shape violation: missing numeric `hits`');
    }

    if (searchParams.facets && searchParams.facets.length > 0 && !Array.isArray(json.facets)) {
      throw new Error(
        'SearchBlox response shape violation: facets were requested but `facets` is missing or not an array'
      );
    }

    return json;
  };

  return {
    search,
  };
};

export const createProxy = (config: SearchBloxConfig) => {
  const { col, querykey, endpoint } = config;
  const sbxLogger = logger.child({ id: 'searchblox' });
  const endpointPath = URL.parse(endpoint)?.pathname.replace(/\/+$/, '') || '/rest/v2/api/search';

  return async (req: NextApiRequest, res: NextApiResponse) => {
    try {
      let reqUrl = new URL(req.url as string, 'http://localhost');
      reqUrl = new URL(endpointPath + reqUrl.search, endpoint);
      reqUrl.searchParams.set('col', col);
      reqUrl.searchParams.set('querykey', querykey);
      if (!reqUrl.searchParams.has('default')) {
        reqUrl.searchParams.set('default', 'AND');
      }

      const response = await fetch(reqUrl, {
        method: 'GET',
        headers: {
          'User-Agent': 'ONIWP SearchBlox',
        },
      });
      const json = (await response.json()) as SearchBloxResponse<SearchBloxResult>;

      res.status(200).json(json);
    } catch (err) {
      sbxLogger.error(err);

      res.status(500).end();
    }
  };
};
